import { ComponentType, FC, ReactElement, useRef, useState } from 'react';
import * as PrimitiveDropdownMenu from '@radix-ui/react-dropdown-menu';
import { cn, Tooltip } from '@fe-learner-common';

export interface IDropdownMenuItemComponent
  extends PrimitiveDropdownMenu.DropdownMenuItemProps {
  className?: string;
  onSelect: () => void;
}

export interface IMenuItem {
  key: string;
  Component: ComponentType<IDropdownMenuItemComponent>;
}

type CourseCardMenuProps = {
  items: IMenuItem[];
  trigger: ReactElement;
  tooltipText?: string;
  contentClassName?: string;
  onOpenChange?: (isOpen: boolean) => void;
  container: HTMLElement;
};

export const DropdownMenu: FC<CourseCardMenuProps> = ({
  items,
  trigger,
  tooltipText,
  contentClassName,
  onOpenChange,
  container,
}) => {
  const blockAutoFocus = useRef(false);
  const [open, setOpen] = useState(false);

  const handleSetOpen = (isOpen: boolean) => {
    setOpen(isOpen);
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  };

  if (!items || !items.length) return null;

  const Button = (
    <PrimitiveDropdownMenu.Trigger asChild>
      {trigger}
    </PrimitiveDropdownMenu.Trigger>
  );

  return (
    <PrimitiveDropdownMenu.Root
      open={open}
      onOpenChange={(_open) => {
        handleSetOpen(_open);
      }}
    >
      {tooltipText ? (
        <Tooltip
          text={tooltipText}
          side="bottom"
          parentOpen={open}
          container={container}
        >
          {Button}
        </Tooltip>
      ) : (
        Button
      )}

      <PrimitiveDropdownMenu.Portal container={container}>
        <PrimitiveDropdownMenu.Content
          avoidCollisions
          side="bottom"
          loop={true}
          align="start"
          collisionPadding={12}
          className={cn(
            'flex flex-col gap-y-2 rounded-xl bg-neutral-0 p-2 shadow-100',
            contentClassName,
          )}
          onPointerDownOutside={(event) => {
            handleSetOpen(false);
            blockAutoFocus.current = true;
            event.preventDefault();
          }}
          onFocus={() => {
            blockAutoFocus.current = false;
          }}
          onCloseAutoFocus={(event) => {
            if (blockAutoFocus.current) {
              event.preventDefault();
              return;
            }
          }}
        >
          {items.map(({ key, Component }) => (
            <Component
              className="flex-1 cursor-pointer rounded-lg px-4 py-2.5 text-left text-title-sm text-neutral-500 hover:bg-neutral-200 hover:outline-none"
              onSelect={() => {
                handleSetOpen(false);
              }}
              key={key}
            />
          ))}
        </PrimitiveDropdownMenu.Content>
      </PrimitiveDropdownMenu.Portal>
    </PrimitiveDropdownMenu.Root>
  );
};
